import React, { useState, useEffect } from "react";
import {
  useFetchSchemeFiltersMutation,
  useFetchSchemeStatsMutation,
} from "../../features/tenant/scheme/schemeApi";
import {
  MultiSelect,
  SingleSelect,
  ProductCodeSearchSelect,
} from "./FilterComponents";
import {
  getToken,
  getTenantId,
  getAppUserTypes,
  orAll,
  POINTS_RANGE_OPTIONS,
} from "./helpers";

// ── Date preset helper ───────────────────────────────────────────────────────
const getPresetDates = (preset) => {
  const today = new Date();
  const fmt = (d) => d.toISOString().split("T")[0];

  if (preset === "30d") {
    const start = new Date(today);
    start.setDate(today.getDate() - 30);
    return { start: fmt(start), end: fmt(today) };
  }
  if (preset === "90d") {
    const start = new Date(today);
    start.setDate(today.getDate() - 90);
    return { start: fmt(start), end: fmt(today) };
  }
  if (preset === "month") {
    const start = new Date(today.getFullYear(), today.getMonth(), 1);
    return { start: fmt(start), end: fmt(today) };
  }
  return { start: "", end: "" };
};

const PRESETS = [
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "month", label: "This month" },
];

const PANEL_STYLES = [
  {
    header: "bg-blue-600",
    btn: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-300",
    label: "A",
  },
  {
    header: "bg-emerald-600",
    btn: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-300",
    label: "B",
  },
];

// ── Stats Table (sortable) ────────────────────────────────────────────────────
const StatsTable = ({ columns, rows, keyField }) => {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const sorted = sortKey
    ? [...rows].sort((a, b) => {
        const aVal = a[sortKey] ?? "";
        const bVal = b[sortKey] ?? "";
        if (typeof aVal === "number" && typeof bVal === "number") {
          return sortDir === "asc" ? aVal - bVal : bVal - aVal;
        }
        return sortDir === "asc"
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      })
    : rows;

  const topRowKey = sorted[0]?.[keyField];

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 border-b border-gray-200">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col.key)}
                className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none transition-colors"
              >
                <span className="flex items-center gap-1">
                  {col.label}
                  <span className="text-gray-300">
                    {sortKey === col.key
                      ? sortDir === "asc"
                        ? "↑"
                        : "↓"
                      : "↕"}
                  </span>
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {sorted.map((row, i) => (
            <tr key={row[keyField] ?? i} className="hover:bg-gray-50">
              {columns.map((col, ci) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${col.colored ? (Number(row[col.key]) < 0 ? "text-red-600" : "text-green-700") : "text-gray-700"} ${ci === 0 && row[keyField] === topRowKey ? "font-bold" : ""}`}
                >
                  {ci === 0 && row[keyField] === topRowKey && (
                    <span className="mr-1">🥇</span>
                  )}
                  {typeof row[col.key] === "number"
                    ? Number(row[col.key].toFixed(2)).toLocaleString()
                    : (row[col.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── Empty section placeholder ─────────────────────────────────────────────────
const SectionEmpty = ({ label }) => (
  <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
    <svg
      className="w-4 h-4 text-gray-300 flex-shrink-0"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
      />
    </svg>
    <div>
      <p className="text-xs font-semibold text-gray-400">
        No {label} data for selected filters
      </p>
      <p className="text-xs text-gray-300 mt-0.5">
        Try adjusting or clearing your filters
      </p>
    </div>
  </div>
);

// ── Stats Display (tables on page) ────────────────────────────────────────────
const StatsDisplay = ({ stats }) => {
  const s = stats?.body?.filtered_stats ?? stats?.filtered_stats;
  const pointsRangeDistribution = s?.points_range_distribution;
  if (!s) return null;

  const stateCols = [
    { key: "state", label: "State" },
    { key: "total_participants", label: "Participants" },
    { key: "total_points_earned", label: "Earned", colored: true },
    { key: "total_points_redeemed", label: "Redeemed", colored: true },
  ];
  const productCols = [
    { key: "product_code", label: "Product Code" },
    { key: "total_scans", label: "Scans" },
    { key: "unique_users", label: "Users" },
    { key: "total_points_earned", label: "Earned", colored: true },
  ];
  const categoryCols = [
    { key: "category_name", label: "Category" },
    { key: "total_scans", label: "Scans" },
    { key: "unique_users", label: "Users" },
    { key: "total_points_earned", label: "Earned", colored: true },
  ];
  const pointsRangeCols = [
    { key: "range", label: "Points Range" },
    { key: "count", label: "Users" },
  ];

  return (
    <div className="mt-5 space-y-5 border-t border-gray-100 pt-4">
      <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
        Results
      </p>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">State-wise</p>
        {s?.states?.length > 0 ? (
          <StatsTable columns={stateCols} rows={s.states} keyField="state" />
        ) : (
          <SectionEmpty label="state" />
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">
          Product Codes
        </p>
        {s?.product_codes?.length > 0 ? (
          <StatsTable
            columns={productCols}
            rows={s.product_codes}
            keyField="product_code"
          />
        ) : (
          <SectionEmpty label="product code" />
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">Categories</p>
        {s?.product_categories?.length > 0 ? (
          <StatsTable
            columns={categoryCols}
            rows={s.product_categories}
            keyField="category_name"
          />
        ) : (
          <SectionEmpty label="category" />
        )}
      </div>

      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">
          Points Earned In Range
        </p>
        {pointsRangeDistribution?.length > 0 ? (
          <StatsTable
            columns={pointsRangeCols}
            rows={pointsRangeDistribution}
            keyField="range"
          />
        ) : (
          <SectionEmpty label="points range" />
        )}
      </div>
    </div>
  );
};

// ── Scheme Panel ──────────────────────────────────────────────────────────────
const SchemePanel = ({
  scheme,
  index,
  onStatsLoaded,
  onRemove,
  canRemove,
  totalPanels,
}) => {
  const style = PANEL_STYLES[index % PANEL_STYLES.length];
  const isSingle = totalPanels === 1;

  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedProductCodes, setSelectedProductCodes] = useState([]);
  const [selectedProductCategories, setSelectedProductCategories] = useState(
    [],
  );
  const [selectedUserTypeIds, setSelectedUserTypeIds] = useState([]);
  const [selectedPointsRange, setSelectedPointsRange] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [activePreset, setActivePreset] = useState("");
  const [panelStats, setPanelStats] = useState(null);

  const [fetchFilters, { data: filtersData, isLoading: filtersLoading }] =
    useFetchSchemeFiltersMutation();
  const [fetchStats, { isLoading: statsLoading }] =
    useFetchSchemeStatsMutation();

  const appUserTypes = getAppUserTypes();
  const userTypeOptions = appUserTypes.map((u) => ({
    value: String(u.user_type_id),
    label: u.user_type,
  }));

  useEffect(() => {
    if (scheme?.id) {
      setPanelStats(null);
      onStatsLoaded(null);
      setSelectedStates([]);
      setSelectedProductCodes([]);
      setSelectedProductCategories([]);
      setSelectedUserTypeIds([]);
      setSelectedPointsRange("");
      setStartDate("");
      setEndDate("");
      setActivePreset("");
      fetchFilters({
        token: getToken(),
        tenant_id: getTenantId(),
        scheme_id: scheme.id,
      });
    }
  }, [scheme?.id]);

  const filterOptions = filtersData?.body ?? filtersData ?? {};
  const stateOptions = (filterOptions.states ?? []).filter(Boolean);
  const categoryOptions = (filterOptions.product_categories ?? []).filter(
    Boolean,
  );

  const handlePreset = (key) => {
    if (key === activePreset) {
      // clicking same preset clears it
      setActivePreset("");
      setStartDate("");
      setEndDate("");
    } else {
      const { start, end } = getPresetDates(key);
      setActivePreset(key);
      setStartDate(start);
      setEndDate(end);
    }
  };

  const handleReset = () => {
    setSelectedStates([]);
    setSelectedProductCodes([]);
    setSelectedProductCategories([]);
    setSelectedUserTypeIds([]);
    setSelectedPointsRange("");
    setStartDate("");
    setEndDate("");
    setActivePreset("");
    setPanelStats(null);
    onStatsLoaded(null);
  };

  const handleViewStats = async () => {
    const result = await fetchStats({
      token: getToken(),
      tenant_id: getTenantId(),
      body: {
        scheme_id: scheme.id,
        states: orAll(selectedStates),
        product_codes: orAll(selectedProductCodes),
        product_categories: orAll(selectedProductCategories),
        user_type_ids: orAll(selectedUserTypeIds),
        points_range: selectedPointsRange || "all",
        start_date: startDate || undefined,
        end_date: endDate || undefined,
      },
    });
    if (result?.data) {
      setPanelStats(result.data);
      onStatsLoaded(result.data);
    }
  };

  const inputCls =
    "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent";
  const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

  return (
    <div className="flex flex-col bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
      {/* Header */}
      <div
        className={`${style.header} px-5 py-3 flex items-center justify-between`}
      >
        <div>
          {!isSingle && (
            <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
              Scheme {style.label}
            </p>
          )}
          <h3 className="text-base font-bold text-white">{scheme.name}</h3>
        </div>
        {canRemove && (
          <button
            onClick={onRemove}
            className="text-white/70 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      <div className="p-4 flex flex-col gap-3">
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
          Filters
        </p>

        <MultiSelect
          label="User Types *"
          options={userTypeOptions}
          selected={selectedUserTypeIds}
          onChange={setSelectedUserTypeIds}
          loading={false}
        />
        <MultiSelect
          label="States"
          options={stateOptions}
          selected={selectedStates}
          onChange={setSelectedStates}
          loading={filtersLoading}
        />
        <ProductCodeSearchSelect
          schemeId={scheme.id}
          selected={selectedProductCodes}
          onChange={setSelectedProductCodes}
        />
        <MultiSelect
          label="Product Categories"
          options={categoryOptions}
          selected={selectedProductCategories}
          onChange={setSelectedProductCategories}
          loading={filtersLoading}
        />
        <SingleSelect
          label="Points Earned In Range"
          options={POINTS_RANGE_OPTIONS}
          value={selectedPointsRange}
          onChange={setSelectedPointsRange}
          placeholder="All ranges"
        />

        {/* Date presets */}
        <div>
          <label className={labelCls}>Date Range</label>
          <div className="flex gap-2 mb-2 flex-wrap">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                onClick={() => handlePreset(p.key)}
                className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
                  activePreset === p.key
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                className={inputCls}
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setActivePreset("");
                }}
              />
            </div>
            <div>
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                className={inputCls}
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setActivePreset("");
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleViewStats}
            disabled={statsLoading || filtersLoading}
            className={`flex-1 text-white ${style.btn} disabled:opacity-60 focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 transition-colors`}
          >
            {statsLoading ? "Loading..." : "View Stats"}
          </button>
          <button
            onClick={handleReset}
            title="Reset all filters"
            className="px-3 py-2.5 text-gray-500 bg-gray-100 hover:bg-gray-200 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors text-sm font-medium"
          >
            Reset
          </button>
        </div>

        {/* Tables rendered directly on the page — not in modal */}
        {panelStats && <StatsDisplay stats={panelStats} />}
      </div>
    </div>
  );
};

export default SchemePanel;

// import React, { useState, useEffect } from "react";
// import {
//   useFetchSchemeFiltersMutation,
//   useFetchSchemeStatsMutation,
// } from "../../features/tenant/scheme/schemeApi";
// import {
//   MultiSelect,
//   SingleSelect,
//   ProductCodeSearchSelect,
// } from "./FilterComponents";
// import {
//   getToken,
//   getTenantId,
//   getAppUserTypes,
//   orAll,
//   POINTS_RANGE_OPTIONS,
// } from "./helpers";

// const PANEL_STYLES = [
//   {
//     header: "bg-blue-600",
//     btn: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-300",
//     label: "A",
//   },
//   {
//     header: "bg-emerald-600",
//     btn: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-300",
//     label: "B",
//   },
// ];

// // ── Stats Table ───────────────────────────────────────────────────────────────
// const StatsTable = ({ columns, rows, keyField }) => (
//   <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
//     <table className="min-w-full text-sm">
//       <thead className="bg-gray-50 border-b border-gray-200">
//         <tr>
//           {columns.map((col) => (
//             <th
//               key={col.key}
//               className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap"
//             >
//               {col.label}
//             </th>
//           ))}
//         </tr>
//       </thead>
//       <tbody className="divide-y divide-gray-100 bg-white">
//         {rows.map((row, i) => (
//           <tr key={row[keyField] ?? i} className="hover:bg-gray-50">
//             {columns.map((col) => (
//               <td
//                 key={col.key}
//                 className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${col.colored ? (Number(row[col.key]) < 0 ? "text-red-600" : "text-green-700") : "text-gray-700"}`}
//               >
//                 {typeof row[col.key] === "number"
//                   ? Number(row[col.key].toFixed(2)).toLocaleString()
//                   : (row[col.key] ?? "—")}
//               </td>
//             ))}
//           </tr>
//         ))}
//       </tbody>
//     </table>
//   </div>
// );

// // ── Stats Display (tables on page) ────────────────────────────────────────────
// const StatsDisplay = ({ stats }) => {
//   const s = stats?.body?.filtered_stats ?? stats?.filtered_stats;
//   const pointsRangeDistribution = s?.points_range_distribution;
//   if (!s) return null;

//   const stateCols = [
//     { key: "state", label: "State" },
//     { key: "total_participants", label: "Participants" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//     { key: "total_points_redeemed", label: "Redeemed", colored: true },
//   ];
//   const productCols = [
//     { key: "product_code", label: "Product Code" },
//     { key: "total_scans", label: "Scans" },
//     { key: "unique_users", label: "Users" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//   ];
//   const categoryCols = [
//     { key: "category_name", label: "Category" },
//     { key: "total_scans", label: "Scans" },
//     { key: "unique_users", label: "Users" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//   ];
//   const pointsRangeCols = [
//     { key: "range", label: "Points Range" },
//     { key: "count", label: "Users" },
//   ];

//   return (
//     <div className="mt-5 space-y-5 border-t border-gray-100 pt-4">
//       <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
//         Results
//       </p>

//       {s?.states?.length > 0 && (
//         <div>
//           <p className="text-xs font-semibold text-gray-500 mb-1">State-wise</p>
//           <StatsTable columns={stateCols} rows={s.states} keyField="state" />
//         </div>
//       )}
//       {s?.product_codes?.length > 0 && (
//         <div>
//           <p className="text-xs font-semibold text-gray-500 mb-1">
//             Product Codes
//           </p>
//           <StatsTable
//             columns={productCols}
//             rows={s.product_codes}
//             keyField="product_code"
//           />
//         </div>
//       )}
//       {s?.product_categories?.length > 0 && (
//         <div>
//           <p className="text-xs font-semibold text-gray-500 mb-1">Categories</p>
//           <StatsTable
//             columns={categoryCols}
//             rows={s.product_categories}
//             keyField="category_name"
//           />
//         </div>
//       )}
//       {pointsRangeDistribution?.length > 0 && (
//         <div>
//           <p className="text-xs font-semibold text-gray-500 mb-1">
//             Points Earned In Range
//           </p>
//           <StatsTable
//             columns={pointsRangeCols}
//             rows={pointsRangeDistribution}
//             keyField="range"
//           />
//         </div>
//       )}
//     </div>
//   );
// };

// // ── Scheme Panel ──────────────────────────────────────────────────────────────
// const SchemePanel = ({
//   scheme,
//   index,
//   onStatsLoaded,
//   onRemove,
//   canRemove,
//   totalPanels,
// }) => {
//   const style = PANEL_STYLES[index % PANEL_STYLES.length];
//   const isSingle = totalPanels === 1;

//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedProductCodes, setSelectedProductCodes] = useState([]);
//   const [selectedProductCategories, setSelectedProductCategories] = useState(
//     [],
//   );
//   const [selectedUserTypeIds, setSelectedUserTypeIds] = useState([]);
//   const [selectedPointsRange, setSelectedPointsRange] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [panelStats, setPanelStats] = useState(null);

//   const [fetchFilters, { data: filtersData, isLoading: filtersLoading }] =
//     useFetchSchemeFiltersMutation();
//   const [fetchStats, { isLoading: statsLoading }] =
//     useFetchSchemeStatsMutation();

//   const appUserTypes = getAppUserTypes();
//   const userTypeOptions = appUserTypes.map((u) => ({
//     value: String(u.user_type_id),
//     label: u.user_type,
//   }));

//   useEffect(() => {
//     if (scheme?.id) {
//       setPanelStats(null);
//       onStatsLoaded(null);
//       setSelectedStates([]);
//       setSelectedProductCodes([]);
//       setSelectedProductCategories([]);
//       setSelectedUserTypeIds([]);
//       setSelectedPointsRange("");
//       setStartDate("");
//       setEndDate("");
//       fetchFilters({
//         token: getToken(),
//         tenant_id: getTenantId(),
//         scheme_id: scheme.id,
//       });
//     }
//   }, [scheme?.id]);

//   const filterOptions = filtersData?.body ?? filtersData ?? {};
//   const stateOptions = (filterOptions.states ?? []).filter(Boolean);
//   const categoryOptions = (filterOptions.product_categories ?? []).filter(
//     Boolean,
//   );

//   const handleViewStats = async () => {
//     const result = await fetchStats({
//       token: getToken(),
//       tenant_id: getTenantId(),
//       body: {
//         scheme_id: scheme.id,
//         states: orAll(selectedStates),
//         product_codes: orAll(selectedProductCodes),
//         product_categories: orAll(selectedProductCategories),
//         user_type_ids: orAll(selectedUserTypeIds),
//         points_range: selectedPointsRange || "all",
//         start_date: startDate || undefined,
//         end_date: endDate || undefined,
//       },
//     });
//     if (result?.data) {
//       setPanelStats(result.data);
//       onStatsLoaded(result.data);
//     }
//   };

//   const inputCls =
//     "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent";
//   const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

//   return (
//     <div className="flex flex-col bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
//       {/* Header */}
//       <div
//         className={`${style.header} px-5 py-3 flex items-center justify-between`}
//       >
//         <div>
//           {!isSingle && (
//             <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
//               Scheme {style.label}
//             </p>
//           )}
//           <h3 className="text-base font-bold text-white">{scheme.name}</h3>
//         </div>
//         {canRemove && (
//           <button
//             onClick={onRemove}
//             className="text-white/70 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
//           >
//             <svg
//               className="w-4 h-4"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M6 18L18 6M6 6l12 12"
//               />
//             </svg>
//           </button>
//         )}
//       </div>

//       <div className="p-4 flex flex-col gap-3">
//         <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
//           Filters
//         </p>

//         <MultiSelect
//           label="User Types *"
//           options={userTypeOptions}
//           selected={selectedUserTypeIds}
//           onChange={setSelectedUserTypeIds}
//           loading={false}
//         />
//         <MultiSelect
//           label="States"
//           options={stateOptions}
//           selected={selectedStates}
//           onChange={setSelectedStates}
//           loading={filtersLoading}
//         />
//         <ProductCodeSearchSelect
//           schemeId={scheme.id}
//           selected={selectedProductCodes}
//           onChange={setSelectedProductCodes}
//         />
//         <MultiSelect
//           label="Product Categories"
//           options={categoryOptions}
//           selected={selectedProductCategories}
//           onChange={setSelectedProductCategories}
//           loading={filtersLoading}
//         />
//         <SingleSelect
//           label="Points Earned In Range"
//           options={POINTS_RANGE_OPTIONS}
//           value={selectedPointsRange}
//           onChange={setSelectedPointsRange}
//           placeholder="All ranges"
//         />

//         <div className="grid grid-cols-2 gap-3">
//           <div>
//             <label className={labelCls}>Start Date</label>
//             <input
//               type="date"
//               className={inputCls}
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>
//           <div>
//             <label className={labelCls}>End Date</label>
//             <input
//               type="date"
//               className={inputCls}
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>
//         </div>

//         <button
//           onClick={handleViewStats}
//           disabled={statsLoading || filtersLoading}
//           className={`text-white ${style.btn} disabled:opacity-60 focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 transition-colors w-full`}
//         >
//           {statsLoading ? "Loading..." : "View Stats"}
//         </button>

//         {/* Tables rendered directly on the page — not in modal */}
//         {panelStats && <StatsDisplay stats={panelStats} />}
//       </div>
//     </div>
//   );
// };

// export default SchemePanel;

// import React, { useState, useEffect } from "react";
// import {
//   useFetchSchemeFiltersMutation,
//   useFetchSchemeStatsMutation,
// } from "../../features/tenant/scheme/schemeApi";
// import {
//   MultiSelect,
//   SingleSelect,
//   ProductCodeSearchSelect,
// } from "./FilterComponents";
// import {
//   getToken,
//   getTenantId,
//   getAppUserTypes,
//   orAll,
//   POINTS_RANGE_OPTIONS,
// } from "./helpers";

// // ── Date preset helper ───────────────────────────────────────────────────────
// const getPresetDates = (preset) => {
//   const today = new Date();
//   const fmt = (d) => d.toISOString().split("T")[0];

//   if (preset === "30d") {
//     const start = new Date(today);
//     start.setDate(today.getDate() - 30);
//     return { start: fmt(start), end: fmt(today) };
//   }
//   if (preset === "90d") {
//     const start = new Date(today);
//     start.setDate(today.getDate() - 90);
//     return { start: fmt(start), end: fmt(today) };
//   }
//   if (preset === "month") {
//     const start = new Date(today.getFullYear(), today.getMonth(), 1);
//     return { start: fmt(start), end: fmt(today) };
//   }
//   return { start: "", end: "" };
// };

// const PRESETS = [
//   { key: "30d", label: "Last 30 days" },
//   { key: "90d", label: "Last 90 days" },
//   { key: "month", label: "This month" },
// ];

// const PANEL_STYLES = [
//   {
//     header: "bg-blue-600",
//     btn: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-300",
//     label: "A",
//   },
//   {
//     header: "bg-emerald-600",
//     btn: "bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-300",
//     label: "B",
//   },
// ];

// // ── Stats Table (sortable) ────────────────────────────────────────────────────
// const StatsTable = ({ columns, rows, keyField }) => {
//   const [sortKey, setSortKey] = useState(null);
//   const [sortDir, setSortDir] = useState("desc");

//   const handleSort = (key) => {
//     if (sortKey === key) {
//       setSortDir((d) => (d === "asc" ? "desc" : "asc"));
//     } else {
//       setSortKey(key);
//       setSortDir("desc");
//     }
//   };

//   const sorted = sortKey
//     ? [...rows].sort((a, b) => {
//         const aVal = a[sortKey] ?? "";
//         const bVal = b[sortKey] ?? "";
//         if (typeof aVal === "number" && typeof bVal === "number") {
//           return sortDir === "asc" ? aVal - bVal : bVal - aVal;
//         }
//         return sortDir === "asc"
//           ? String(aVal).localeCompare(String(bVal))
//           : String(bVal).localeCompare(String(aVal));
//       })
//     : rows;

//   return (
//     <div className="overflow-x-auto rounded-lg border border-gray-200 mt-2">
//       <table className="min-w-full text-sm">
//         <thead className="bg-gray-50 border-b border-gray-200">
//           <tr>
//             {columns.map((col) => (
//               <th
//                 key={col.key}
//                 onClick={() => handleSort(col.key)}
//                 className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:bg-gray-100 select-none transition-colors"
//               >
//                 <span className="flex items-center gap-1">
//                   {col.label}
//                   <span className="text-gray-300">
//                     {sortKey === col.key
//                       ? sortDir === "asc"
//                         ? "↑"
//                         : "↓"
//                       : "↕"}
//                   </span>
//                 </span>
//               </th>
//             ))}
//           </tr>
//         </thead>
//         <tbody className="divide-y divide-gray-100 bg-white">
//           {sorted.map((row, i) => (
//             <tr key={row[keyField] ?? i} className="hover:bg-gray-50">
//               {columns.map((col, ci) => (
//                 <td
//                   key={col.key}
//                   className={`px-3 py-2 whitespace-nowrap text-sm font-medium ${col.colored ? (Number(row[col.key]) < 0 ? "text-red-600" : "text-green-700") : "text-gray-700"} ${ci === 0 && i === 0 ? "font-bold" : ""}`}
//                 >
//                   {/* {ci === 0 && i === 0 && <span className="mr-1">🥇</span>} */}
//                   {typeof row[col.key] === "number"
//                     ? Number(row[col.key].toFixed(2)).toLocaleString()
//                     : (row[col.key] ?? "—")}
//                 </td>
//               ))}
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// };

// // ── Empty section placeholder ─────────────────────────────────────────────────
// const SectionEmpty = ({ label }) => (
//   <div className="flex items-center gap-3 rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-3">
//     <svg
//       className="w-4 h-4 text-gray-300 flex-shrink-0"
//       fill="none"
//       stroke="currentColor"
//       viewBox="0 0 24 24"
//     >
//       <path
//         strokeLinecap="round"
//         strokeLinejoin="round"
//         strokeWidth={1.5}
//         d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z"
//       />
//     </svg>
//     <div>
//       <p className="text-xs font-semibold text-gray-400">
//         No {label} data for selected filters
//       </p>
//       <p className="text-xs text-gray-300 mt-0.5">
//         Try adjusting or clearing your filters
//       </p>
//     </div>
//   </div>
// );

// // ── Stats Display (tables on page) ────────────────────────────────────────────
// const StatsDisplay = ({ stats }) => {
//   const s = stats?.body?.filtered_stats ?? stats?.filtered_stats;
//   const pointsRangeDistribution = s?.points_range_distribution;
//   if (!s) return null;

//   const stateCols = [
//     { key: "state", label: "State" },
//     { key: "total_participants", label: "Participants" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//     { key: "total_points_redeemed", label: "Redeemed", colored: true },
//   ];
//   const productCols = [
//     { key: "product_code", label: "Product Code" },
//     { key: "total_scans", label: "Scans" },
//     { key: "unique_users", label: "Users" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//   ];
//   const categoryCols = [
//     { key: "category_name", label: "Category" },
//     { key: "total_scans", label: "Scans" },
//     { key: "unique_users", label: "Users" },
//     { key: "total_points_earned", label: "Earned", colored: true },
//   ];
//   const pointsRangeCols = [
//     { key: "range", label: "Points Range" },
//     { key: "count", label: "Users" },
//   ];

//   return (
//     <div className="mt-5 space-y-5 border-t border-gray-100 pt-4">
//       <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
//         Results
//       </p>

//       <div>
//         <p className="text-xs font-semibold text-gray-500 mb-1">State-wise</p>
//         {s?.states?.length > 0 ? (
//           <StatsTable columns={stateCols} rows={s.states} keyField="state" />
//         ) : (
//           <SectionEmpty label="state" />
//         )}
//       </div>

//       <div>
//         <p className="text-xs font-semibold text-gray-500 mb-1">
//           Product Codes
//         </p>
//         {s?.product_codes?.length > 0 ? (
//           <StatsTable
//             columns={productCols}
//             rows={s.product_codes}
//             keyField="product_code"
//           />
//         ) : (
//           <SectionEmpty label="product code" />
//         )}
//       </div>

//       <div>
//         <p className="text-xs font-semibold text-gray-500 mb-1">Categories</p>
//         {s?.product_categories?.length > 0 ? (
//           <StatsTable
//             columns={categoryCols}
//             rows={s.product_categories}
//             keyField="category_name"
//           />
//         ) : (
//           <SectionEmpty label="category" />
//         )}
//       </div>

//       <div>
//         <p className="text-xs font-semibold text-gray-500 mb-1">
//           Points Earned In Range
//         </p>
//         {pointsRangeDistribution?.length > 0 ? (
//           <StatsTable
//             columns={pointsRangeCols}
//             rows={pointsRangeDistribution}
//             keyField="range"
//           />
//         ) : (
//           <SectionEmpty label="points range" />
//         )}
//       </div>
//     </div>
//   );
// };

// // ── Scheme Panel ──────────────────────────────────────────────────────────────
// const SchemePanel = ({
//   scheme,
//   index,
//   onStatsLoaded,
//   onRemove,
//   canRemove,
//   totalPanels,
// }) => {
//   const style = PANEL_STYLES[index % PANEL_STYLES.length];
//   const isSingle = totalPanels === 1;

//   const [selectedStates, setSelectedStates] = useState([]);
//   const [selectedProductCodes, setSelectedProductCodes] = useState([]);
//   const [selectedProductCategories, setSelectedProductCategories] = useState(
//     [],
//   );
//   const [selectedUserTypeIds, setSelectedUserTypeIds] = useState([]);
//   const [selectedPointsRange, setSelectedPointsRange] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");
//   const [activePreset, setActivePreset] = useState("");
//   const [panelStats, setPanelStats] = useState(null);

//   const [fetchFilters, { data: filtersData, isLoading: filtersLoading }] =
//     useFetchSchemeFiltersMutation();
//   const [fetchStats, { isLoading: statsLoading }] =
//     useFetchSchemeStatsMutation();

//   const appUserTypes = getAppUserTypes();
//   const userTypeOptions = appUserTypes.map((u) => ({
//     value: String(u.user_type_id),
//     label: u.user_type,
//   }));

//   useEffect(() => {
//     if (scheme?.id) {
//       setPanelStats(null);
//       onStatsLoaded(null);
//       setSelectedStates([]);
//       setSelectedProductCodes([]);
//       setSelectedProductCategories([]);
//       setSelectedUserTypeIds([]);
//       setSelectedPointsRange("");
//       setStartDate("");
//       setEndDate("");
//       setActivePreset("");
//       fetchFilters({
//         token: getToken(),
//         tenant_id: getTenantId(),
//         scheme_id: scheme.id,
//       });
//     }
//   }, [scheme?.id]);

//   const filterOptions = filtersData?.body ?? filtersData ?? {};
//   const stateOptions = (filterOptions.states ?? []).filter(Boolean);
//   const categoryOptions = (filterOptions.product_categories ?? []).filter(
//     Boolean,
//   );

//   const handlePreset = (key) => {
//     if (key === activePreset) {
//       // clicking same preset clears it
//       setActivePreset("");
//       setStartDate("");
//       setEndDate("");
//     } else {
//       const { start, end } = getPresetDates(key);
//       setActivePreset(key);
//       setStartDate(start);
//       setEndDate(end);
//     }
//   };

//   const handleReset = () => {
//     setSelectedStates([]);
//     setSelectedProductCodes([]);
//     setSelectedProductCategories([]);
//     setSelectedUserTypeIds([]);
//     setSelectedPointsRange("");
//     setStartDate("");
//     setEndDate("");
//     setActivePreset("");
//     setPanelStats(null);
//     onStatsLoaded(null);
//   };

//   const handleViewStats = async () => {
//     const result = await fetchStats({
//       token: getToken(),
//       tenant_id: getTenantId(),
//       body: {
//         scheme_id: scheme.id,
//         states: orAll(selectedStates),
//         product_codes: orAll(selectedProductCodes),
//         product_categories: orAll(selectedProductCategories),
//         user_type_ids: orAll(selectedUserTypeIds),
//         points_range: selectedPointsRange || "all",
//         start_date: startDate || undefined,
//         end_date: endDate || undefined,
//       },
//     });
//     if (result?.data) {
//       setPanelStats(result.data);
//       onStatsLoaded(result.data);
//     }
//   };

//   const inputCls =
//     "w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent";
//   const labelCls = "block text-xs font-semibold text-gray-500 mb-1";

//   return (
//     <div className="flex flex-col bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
//       {/* Header */}
//       <div
//         className={`${style.header} px-5 py-3 flex items-center justify-between`}
//       >
//         <div>
//           {!isSingle && (
//             <p className="text-xs font-semibold uppercase tracking-widest text-white/70">
//               Scheme {style.label}
//             </p>
//           )}
//           <h3 className="text-base font-bold text-white">{scheme.name}</h3>
//         </div>
//         {canRemove && (
//           <button
//             onClick={onRemove}
//             className="text-white/70 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
//           >
//             <svg
//               className="w-4 h-4"
//               fill="none"
//               stroke="currentColor"
//               viewBox="0 0 24 24"
//             >
//               <path
//                 strokeLinecap="round"
//                 strokeLinejoin="round"
//                 strokeWidth={2}
//                 d="M6 18L18 6M6 6l12 12"
//               />
//             </svg>
//           </button>
//         )}
//       </div>

//       <div className="p-4 flex flex-col gap-3">
//         <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
//           Filters
//         </p>

//         <MultiSelect
//           label="User Types *"
//           options={userTypeOptions}
//           selected={selectedUserTypeIds}
//           onChange={setSelectedUserTypeIds}
//           loading={false}
//         />
//         <MultiSelect
//           label="States"
//           options={stateOptions}
//           selected={selectedStates}
//           onChange={setSelectedStates}
//           loading={filtersLoading}
//         />
//         <ProductCodeSearchSelect
//           schemeId={scheme.id}
//           selected={selectedProductCodes}
//           onChange={setSelectedProductCodes}
//         />
//         <MultiSelect
//           label="Product Categories"
//           options={categoryOptions}
//           selected={selectedProductCategories}
//           onChange={setSelectedProductCategories}
//           loading={filtersLoading}
//         />
//         <SingleSelect
//           label="Points Earned In Range"
//           options={POINTS_RANGE_OPTIONS}
//           value={selectedPointsRange}
//           onChange={setSelectedPointsRange}
//           placeholder="All ranges"
//         />

//         {/* Date presets */}
//         <div>
//           <label className={labelCls}>Date Range</label>
//           <div className="flex gap-2 mb-2 flex-wrap">
//             {PRESETS.map((p) => (
//               <button
//                 key={p.key}
//                 type="button"
//                 onClick={() => handlePreset(p.key)}
//                 className={`text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
//                   activePreset === p.key
//                     ? "bg-blue-600 text-white border-blue-600"
//                     : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
//                 }`}
//               >
//                 {p.label}
//               </button>
//             ))}
//           </div>
//           <div className="grid grid-cols-2 gap-3">
//             <div>
//               <label className={labelCls}>Start Date</label>
//               <input
//                 type="date"
//                 className={inputCls}
//                 value={startDate}
//                 onChange={(e) => {
//                   setStartDate(e.target.value);
//                   setActivePreset("");
//                 }}
//               />
//             </div>
//             <div>
//               <label className={labelCls}>End Date</label>
//               <input
//                 type="date"
//                 className={inputCls}
//                 value={endDate}
//                 onChange={(e) => {
//                   setEndDate(e.target.value);
//                   setActivePreset("");
//                 }}
//               />
//             </div>
//           </div>
//         </div>

//         <div className="flex gap-2">
//           <button
//             onClick={handleViewStats}
//             disabled={statsLoading || filtersLoading}
//             className={`flex-1 text-white ${style.btn} disabled:opacity-60 focus:ring-2 focus:outline-none font-medium rounded-lg text-sm px-5 py-2.5 transition-colors`}
//           >
//             {statsLoading ? "Loading..." : "View Stats"}
//           </button>
//           <button
//             onClick={handleReset}
//             title="Reset all filters"
//             className="px-3 py-2.5 text-gray-500 bg-gray-100 hover:bg-gray-200 hover:text-gray-700 border border-gray-200 rounded-lg transition-colors text-sm font-medium"
//           >
//             Reset
//           </button>
//         </div>

//         {/* Tables rendered directly on the page — not in modal */}
//         {panelStats && <StatsDisplay stats={panelStats} />}
//       </div>
//     </div>
//   );
// };

// export default SchemePanel;
